import React, { useEffect, useState } from "react";
import AuthenticityTokenField from "../../../components/forms/AuthenticityTokenField";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import {
  FieldLabel,
  TextAreaInput,
  TextInput,
} from "../../../components/ui/FormControls";
import { ssrFormattedDateOr } from "../../../lib/ssrFormattedDate";
import type { CredentialDetail, MainAppPageProps } from "../types";
import { CREDENTIAL_CATEGORY_LABELS } from "@toolkit/mobile-shared";
import { CredentialDateFields } from "./CredentialDateFields";
import { CredentialParsePendingState } from "./CredentialParsePendingState";
import { CredentialRemindersFields } from "./CredentialRemindersFields";
import { CredentialSharePanel } from "./CredentialSharePanel";
import { CredentialStatusBadge } from "./CredentialStatusBadge";

type CredentialShowPageProps = Pick<
  MainAppPageProps,
  "csrfToken" | "routes" | "credentialForm"
> & {
  credential: CredentialDetail;
};

const SAVE_FORM_ID = "credential-save-form";

export const CredentialShowPage: React.FC<CredentialShowPageProps> = ({
  csrfToken,
  routes,
  credential,
  credentialForm,
}) => {
  const [parseStatus, setParseStatus] = useState(credential.parseStatus);
  const action = (
    routes.dashboardCredential ?? "/dashboard/credentials/:id"
  ).replace(":id", credential.id);
  const listPath = routes.dashboardCredentials ?? "/dashboard/credentials";
  const pending = parseStatus === "pending";
  const failed = parseStatus === "failed";
  const canEdit = credential.canEdit !== false;

  useEffect(() => {
    if (parseStatus !== "pending") return undefined;
    const timer = window.setInterval(async () => {
      const response = await fetch(`${action}.json`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      const payload = (await response.json()) as CredentialDetail;
      if (payload.parseStatus && payload.parseStatus !== "pending") {
        window.location.reload();
      }
    }, 1200);
    return () => window.clearInterval(timer);
  }, [action, parseStatus]);

  useEffect(() => {
    setParseStatus(credential.parseStatus);
  }, [credential.parseStatus]);

  return (
    <DashboardPageLayout
      trail={[
        { label: "Credentials", href: listPath },
        { label: credential.name || "Credential" },
      ]}
      title={credential.name || "Credential"}
      subtitle={
        pending
          ? undefined
          : failed
            ? "Couldn't read this photo."
            : credential.issuer || undefined
      }
      headerActions={
        <div className="flex flex-wrap items-center gap-2">
          <CredentialStatusBadge status={credential.status} />
          {canEdit && !pending ? (
            <Button form={SAVE_FORM_ID} type="submit" variant="premium">
              Save
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <Card className="bg-card">
          <CardContent className="relative pt-6">
            {pending ? <CredentialParsePendingState overlay /> : null}
            {credential.imageUrl ? (
              <img
                src={credential.imageUrl}
                alt=""
                className="mb-6 aspect-[1.586/1] w-full rounded-2xl object-cover bg-muted"
              />
            ) : null}
            {pending ? null : (
              <form
                id={SAVE_FORM_ID}
                action={action}
                method="post"
                className="flex flex-col gap-4"
              >
                <AuthenticityTokenField csrfToken={csrfToken} />
                <input type="hidden" name="_method" value="patch" />
                <div>
                  <FieldLabel htmlFor="credential_name">Name</FieldLabel>
                  <TextInput
                    id="credential_name"
                    name="credential[name]"
                    defaultValue={credentialForm?.name ?? credential.name}
                    required
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="credential_issuer">Issuer</FieldLabel>
                  <TextInput
                    id="credential_issuer"
                    name="credential[issuer]"
                    defaultValue={
                      credentialForm?.issuer ?? credential.issuer ?? ""
                    }
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="credential_number">Number</FieldLabel>
                  <TextInput
                    id="credential_number"
                    name="credential[credential_number]"
                    defaultValue={
                      credentialForm?.credentialNumber ??
                      credential.credentialNumber ??
                      ""
                    }
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="credential_category">Type</FieldLabel>
                  <select
                    id="credential_category"
                    name="credential[category]"
                    defaultValue={
                      credentialForm?.category ?? credential.category ?? "other"
                    }
                    className="ds-input"
                  >
                    {Object.entries(CREDENTIAL_CATEGORY_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </select>
                </div>
                <CredentialDateFields
                  layout="grid"
                  defaultIssuedOn={
                    credentialForm?.issuedOn ?? credential.issuedOn ?? ""
                  }
                  defaultExpiresOn={
                    credentialForm?.expiresOn ?? credential.expiresOn ?? ""
                  }
                />
                <div>
                  <FieldLabel htmlFor="credential_notes">Notes</FieldLabel>
                  <TextAreaInput
                    id="credential_notes"
                    name="credential[notes]"
                    rows={4}
                    defaultValue={
                      credentialForm?.notes ?? credential.notes ?? ""
                    }
                  />
                </div>
                <CredentialRemindersFields
                  reminders={credential.reminders}
                  confirmed={credential.confirmed}
                />
              </form>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col gap-6">
          {pending ? null : (
            <CredentialSharePanel
              csrfToken={csrfToken}
              routes={routes}
              credential={credential}
            />
          )}
          {canEdit && !pending ? (
            <form
              action={action}
              method="post"
              onSubmit={(event) => {
                if (
                  !window.confirm(
                    "Remove this credential? This cannot be undone.",
                  )
                ) {
                  event.preventDefault();
                }
              }}
            >
              <AuthenticityTokenField csrfToken={csrfToken} />
              <input type="hidden" name="_method" value="delete" />
              <Button type="submit" variant="destructive">
                Remove credential
              </Button>
            </form>
          ) : null}
          {credential.expiresOnDisplay ? (
            <p className="text-sm text-muted-foreground">
              Expires {ssrFormattedDateOr(credential.expiresOnDisplay)}
            </p>
          ) : null}
        </div>
      </div>
    </DashboardPageLayout>
  );
};
